import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FileSpreadsheet, Trash2, UploadCloud } from "lucide-react";

import { Button, Modal, ModalBody, ModalFooter } from "@/modules/common/components";
import { cn } from "@/libs/cn";

import useFileUpload from "@/modules/common/hooks/useFileUpload";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";
import useImportAneelTariffs from "../../hooks/useImportAneelTariffs";
import { ANEEL_PORTAL_URL } from "../../constants/AneelPortal";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls"];

const isAcceptedFile = (file: File) => ACCEPTED_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));

interface ImportAneelTariffsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ImportAneelTariffsModal = ({ open, onOpenChange }: ImportAneelTariffsModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { upload, start, cancel, reset } = useFileUpload();
  const importMutation = useImportAneelTariffs();

  const isUploading = upload?.status === "uploading" || upload?.status === "confirming";

  const isImporting = importMutation.isPending || (upload?.status === "success" && !importMutation.isSuccess && !importMutation.isError);
  const isProcessing = isUploading || isImporting;

  useEffect(() => {
    if (upload?.status === "success" && upload.attachmentId && !importMutation.isPending && !importMutation.isSuccess) {
      importMutation.mutate({ attachmentId: upload.attachmentId });
    }
  }, [upload?.status, upload?.attachmentId]);

  useEffect(() => {
    if (!importMutation.isSuccess) return;

    toast.success(
      "Importação concluída com sucesso. Os dados tarifários foram atualizados e estão disponíveis para utilização nos cálculos da plataforma.",
    );
    handleClose();
  }, [importMutation.isSuccess]);

  const handleClose = () => {
    cancel();
    reset();
    importMutation.reset();
    setFile(null);
    onOpenChange(false);
  };

  const handleSelectFile = (selected: File | null) => {
    if (!selected) return;

    if (!isAcceptedFile(selected)) {
      toast.error("Formato de arquivo inválido. Envie um arquivo .xlsx ou .xls.");
      return;
    }

    reset();
    importMutation.reset();
    setFile(selected);
  };

  const handleRemoveFile = () => {
    setFile(null);
    cancel();
    reset();
    importMutation.reset();
  };

  const handleImport = () => {
    if (!file) return;
    reset();
    start(file);
  };

  const hasError = upload?.status === "error" || importMutation.isError;
  const errorMessage =
    upload?.status === "error"
      ? upload.error
      : importMutation.error
        ? getApiErrorMessage(importMutation.error, "Verifique o arquivo e tente novamente.")
        : undefined;

  const progressLabel = upload?.status === "uploading" ? "Enviando arquivo..." : isProcessing ? "Processando importação..." : undefined;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && !isImporting && handleClose()}
      title="Importar Tarifas ANEEL"
      description="Faça upload do arquivo de tarifas obtido no portal da ANEEL."
    >
      <ModalBody className="gap-4">
        <div
          onClick={() => !isProcessing && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!isProcessing) setIsDraggingOver(true);
          }}
          onDragLeave={() => setIsDraggingOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingOver(false);
            if (isProcessing) return;
            handleSelectFile(e.dataTransfer.files?.[0] ?? null);
          }}
          className={cn(
            "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors",
            isProcessing ? "cursor-not-allowed opacity-60 border-ui-border" : "cursor-pointer border-ui-border hover:border-ui-primary",
            isDraggingOver && "border-ui-primary bg-ui-hover",
          )}
        >
          <UploadCloud className="h-8 w-8 text-ui-text-muted" />
          <p className="text-sm font-medium text-ui-text">Anexe o arquivo de tarifas ANEEL</p>
          <p className="text-sm text-ui-text-muted">ou arraste até aqui</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.join(",")}
            className="hidden"
            onChange={(e) => handleSelectFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <p className="text-sm text-ui-text-muted">
          Ainda não possui o arquivo?{" "}
          <a href={ANEEL_PORTAL_URL} target="_blank" rel="noreferrer" className="font-medium text-ui-primary underline">
            Acessar Portal ANEEL
          </a>
        </p>

        {file && (
          <div className="flex items-center gap-3 rounded-lg border border-ui-border p-3">
            <FileSpreadsheet className="h-5 w-5 shrink-0 text-ui-text-muted" />
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium text-ui-text">{file.name}</p>
            </div>
            {!isProcessing && (
              <button
                type="button"
                onClick={handleRemoveFile}
                aria-label="Remover arquivo"
                className="shrink-0 text-ui-text-muted cursor-pointer hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {isProcessing && (
          <div className="flex flex-col gap-1.5">
            <div className="h-2 w-full overflow-hidden rounded-full bg-ui-hover">
              <div
                className="h-full rounded-full bg-ui-primary transition-all"
                style={{ width: `${upload?.status === "uploading" ? upload.progress : 100}%` }}
              />
            </div>
            <p className="text-xs text-ui-text-muted">{progressLabel}</p>
          </div>
        )}

        {hasError && errorMessage && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <p className="font-medium">Não foi possível concluir a importação.</p>
            <p>{errorMessage}</p>
          </div>
        )}

        <div className="rounded-lg bg-ui-hover p-3 text-xs text-ui-text-muted">
          <p>• Formatos aceitos: .xlsx, .xls</p>
          <p>• O arquivo será validado antes do processamento.</p>
          <p>• Em caso de erro, seus dados atuais não serão alterados.</p>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button variant="outlined" label="Cancelar" onClick={handleClose} disabled={isImporting} />
        <Button label="Importar" onClick={handleImport} disabled={!file || isProcessing} loading={isProcessing} />
      </ModalFooter>
    </Modal>
  );
};

export default ImportAneelTariffsModal;
